import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Row, Col, Typography, Button as AntButton, Card, Space, Tag, theme } from 'antd';
import {
  SafetyCertificateOutlined,
  TeamOutlined,
  KeyOutlined,
  ExperimentOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { LogoComponent, Login } from '@digiwedge/ui-antd';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { usePasskeyLogin } from './auth/usePasskeyLogin.js';
import { accessControlBranding } from './branding.js';

const { Title, Paragraph } = Typography;

export default function PublicHomePage(): React.ReactElement {
  const { token } = theme.useToken();
  const HERO_IMG = accessControlBranding.heroImageUrl;
  const [showLogin, setShowLogin] = useState(false);
  const isAuthenticated = useGlobalStore((s: GlobalState) => s.isAuthenticated);
  const passkeyAuth = usePasskeyLogin();
  const heroText = token.colorTextLightSolid || token.colorText;
  const heroSubtle = token.colorTextLightSolid || token.colorTextSecondary;

  const withAlpha = (color: string, alpha: number): string => {
    const trimmed = color?.trim?.() || '';
    if (!trimmed) return trimmed;
    if (trimmed.startsWith('rgba(')) {
      const inner = trimmed.slice(5, -1);
      const parts = inner.split(',').map((p) => p.trim());
      if (parts.length >= 3) return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
      return trimmed;
    }
    if (trimmed.startsWith('rgb(')) {
      const inner = trimmed.slice(4, -1);
      const parts = inner.split(',').map((p) => p.trim());
      if (parts.length >= 3) return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
      return trimmed;
    }
    if (trimmed.startsWith('#')) {
      let hex = trimmed.slice(1);
      if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
      if (hex.length === 6) {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
    }
    return trimmed;
  };

  // If a user is already signed in, send them to the dashboard.
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div style={{ background: token.colorBgLayout }}>
      {/* Hero */}
      <section
        style={{
          position: 'relative',
          backgroundImage: `linear-gradient(135deg, ${withAlpha(
            token.colorBgBase,
            0.75,
          )}, ${withAlpha(token.colorBgLayout, 0.35)}), url(${HERO_IMG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: heroText,
          borderBottom: `1px solid ${withAlpha(token.colorBorder, 0.35)}`,
        }}
      >
        <Row gutter={[24, 24]} align="middle" justify="center">
          <Col xs={24} md={20} lg={18} xl={16}>
            <Space size="large" orientation="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <LogoComponent
                  themeMode="dark"
                  size="medium"
                  lightLogoSrc={accessControlBranding.logoLightSrc}
                  darkLogoSrc={accessControlBranding.logoDarkSrc}
                />
                <Tag
                  style={{
                    borderRadius: 999,
                    borderColor: token.colorPrimary,
                    color: token.colorPrimary,
                    background: withAlpha(token.colorPrimary, 0.15),
                  }}
                >
                  {accessControlBranding.adminTagLabel}
                </Tag>
              </div>
              <Title style={{ marginBottom: 0, color: heroText }}>
                {accessControlBranding.heroTitle}
              </Title>
              <Paragraph style={{ fontSize: 18, marginTop: 8, color: heroSubtle }}>
                {accessControlBranding.homeSummary}
              </Paragraph>
              <Space wrap>
                <AntButton type="primary" size="large" onClick={() => setShowLogin(true)}>
                  Sign In
                </AntButton>
                <Link to="/health">
                  <AntButton size="large">System Health</AntButton>
                </Link>
              </Space>
            </Space>
          </Col>
        </Row>
      </section>

      {/* Features */}
      <section style={{ padding: '32px 16px' }}>
        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} md={20} lg={18} xl={16}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} lg={6}>
                <Card hoverable>
                  <Space orientation="vertical" size={8}>
                    <TeamOutlined style={{ fontSize: 24, color: token.colorPrimary }} />
                    <Title level={4} style={{ margin: 0 }}>
                      Tenants & Users
                    </Title>
                    <Paragraph style={{ margin: 0, color: token.colorTextSecondary }}>
                      Unified management of organizations and accounts.
                    </Paragraph>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Card hoverable>
                  <Space orientation="vertical" size={8}>
                    <KeyOutlined style={{ fontSize: 24, color: token.colorPrimary }} />
                    <Title level={4} style={{ margin: 0 }}>
                      Roles & Permissions
                    </Title>
                    <Paragraph style={{ margin: 0, color: token.colorTextSecondary }}>
                      Fine‑grained RBAC with audit trails.
                    </Paragraph>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Card hoverable>
                  <Space orientation="vertical" size={8}>
                    <ExperimentOutlined style={{ fontSize: 24, color: token.colorPrimary }} />
                    <Title level={4} style={{ margin: 0 }}>
                      Feature Flags
                    </Title>
                    <Paragraph style={{ margin: 0, color: token.colorTextSecondary }}>
                      Progressive rollout and targeting built‑in.
                    </Paragraph>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Card hoverable>
                  <Space orientation="vertical" size={8}>
                    <DatabaseOutlined style={{ fontSize: 24, color: token.colorPrimary }} />
                    <Title level={4} style={{ margin: 0 }}>
                      Sessions & Consents
                    </Title>
                    <Paragraph style={{ margin: 0, color: token.colorTextSecondary }}>
                      Security, transparency and compliance.
                    </Paragraph>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </section>

      {/* CTA */}
      <section style={{ padding: '0 16px 56px' }}>
        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} md={20} lg={18} xl={16}>
            <Card>
              <Row align="middle" gutter={[16, 16]}>
                <Col xs={24} lg={18}>
                  <Title level={3} style={{ marginBottom: 8 }}>
                    {accessControlBranding.ctaTitle}
                  </Title>
                  <Paragraph style={{ margin: 0, color: token.colorTextSecondary }}>
                    {accessControlBranding.ctaSubtitle}
                  </Paragraph>
                </Col>
                <Col xs={24} lg={6} style={{ textAlign: 'right' }}>
                  <AntButton
                    type="primary"
                    size="large"
                    icon={<SafetyCertificateOutlined />}
                    onClick={() => setShowLogin(true)}
                  >
                    Sign In
                  </AntButton>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </section>

      {showLogin && (
        <Login
          appName={accessControlBranding.appName}
          asModal
          onCloseModal={() => setShowLogin(false)}
          defaultRemember={true}
          placeholderEmail="you@example.com"
          placeholderPassword="••••••••••"
          passkeyAuth={passkeyAuth ?? undefined}
          layoutVariant="embedded"
          logoLightSrc={accessControlBranding.logoLightSrc}
          logoDarkSrc={accessControlBranding.logoDarkSrc}
        />
      )}
    </div>
  );
}
